import { useEffect, useRef } from 'react';
import { useTeamsStore } from '../../store/teamsStore';
import { roleForTeam } from '../../extensions/bundled/task-manager/boardRoles';
import { withTaskNumbers } from '../../extensions/bundled/task-manager/taskNumbers';

/**
 * Give an open board its task keys (commit ↔ task links, slice 1): the
 * prefix when it has none, a number for every task made before keys existed
 * (or by MCP / the assistant), a fresh number for the later of two tasks that
 * got the same one on two devices, and the high-water mark. ONE normal board
 * edit, so it syncs like any other change. The plan is deterministic
 * (taskNumbers.js), so two devices doing this write the same values.
 *
 * Mirrors useBoardCreatedByFill: never for a read-only board or a team
 * viewer (the server rejects a viewer's push and a rejected op jams the
 * outbox), never while the team role is unknown, at most once per board
 * version in this tab, without bumping `updatedAt` or the undo history.
 * `taken` = the prefixes of the other boards the user can see; null (not
 * loaded yet) waits.
 */
export default function useBoardTaskKeysFill({ board, readOnly, updateBoard, taken }) {
  const teams = useTeamsStore((s) => s.teams);
  const doneRef = useRef(new Map()); // board id → the board object last filled
  const teamId = board?.visibility === 'team' && board?.teamId != null ? String(board.teamId) : null;

  useEffect(() => {
    // taken == null: the other boards are not loaded yet — a prefix chosen now could clash.
    if (!board || readOnly || !taken) return;
    if (teamId) {
      const role = roleForTeam(teams, teamId);
      if (!role || role === 'viewer') return;
    }
    if (doneRef.current.get(board.id) === board) return;
    const next = withTaskNumbers(board, taken);
    doneRef.current.set(board.id, board);
    if (next === board) return;
    updateBoard((prev) => (prev?.id !== board.id ? prev : withTaskNumbers(prev, taken)), { history: false, touch: false });
  }, [board, readOnly, teamId, teams, taken, updateBoard]);
}
